<template>
  <div class="row">
    <div class="col-xs-12">
      <div
        v-show="errorMessage.length"
        ref="errorMessage"
        class="alert alert-danger"
      >
        <button
          class="close"
          @click="clearError('errorMessage')"
        />
        <span>{{ errorMessage }}</span>
      </div>
    </div>
    <div class="col-xs-12">
      <div
        v-show="infoMessage.length"
        ref="infoMessage"
        class="alert alert-info"
      >
        <span>{{ infoMessage }}</span>
      </div>
    </div>
    <div
      v-show="$root.activeLocation.id !== undefined"
      class="col-xs-4"
    >
      <h4>Menus</h4>
    </div>
    <div
      v-show="$root.activeLocation.id !== undefined"
      class="col-xs-4"
    >
      <div class="header">
        <i
          class="fa check"
          :class="{
            'transparent': !menuSelectable(activeMenu) || single,
            'fa-check-square checked': allCategoriesSelected,
            'fa-square-o unchecked': !allCategoriesSelected
          }"
          aria-hidden="true"
          @click="toggleAllCategories()"
        />
        <h4 class="inline-block">
          Categories
        </h4>
      </div>
    </div>
    <div
      v-show="$root.activeLocation.id !== undefined"
      class="col-xs-4"
    >
      <div class="header">
        <i
          class="fa check"
          :class="{
            'transparent' : !activeItems.length || single,
            'fa-check-square checked': allItemsSelected,
            'fa-square-o unchecked': !allItemsSelected
          }"
          aria-hidden="true"
          @click="toggleAllItems"
        />
        <h4 class="inline-block">
          Items
        </h4>
      </div>
    </div>
    <div class="col-xs-4">
      <div
        v-if="loading.menus"
        class="width-100 display-flex justify-content-center"
      >
        <i
          class="margin-top-20 fa fa-spinner fa-pulse fa-fw"
        />
      </div>
      <div
        v-else
        class="dd"
      >
        <ol class="dd-list">
          <li
            v-for="menu in menus"
            :key="menu.id"
            class="dd-item"
          >
            <div
              class="dd-handle pointer v-center"
              :class="{'active': menu.id === activeMenu.id}"
              @click="toggleMenu(menu)"
            >
              <div
                class="v-center"
                :class="{'grey' : menu.items === undefined}"
              >
                <i
                  class="fa check"
                  :class="{
                    'transparent' : !menuSelectable(menu),
                    'fa-check-square checked': menuSelected(menu),
                    'fa-minus-square checked': menuPartiallySelected(menu),
                    'fa-square-o unchecked': !menuSelected(menu)
                  }"
                  aria-hidden="true"
                  @click.stop="toggleAllInMenu(menu)"
                />
                {{ menu.name }}
              </div>
              <span
                class="pull-right transparent"
                :class="{'permanent' : menu.id === activeMenu.id}"
              >
                <i class="fa fa-chevron-right" />
              </span>
            </div>
          </li>
        </ol>
      </div>
    </div>
    <div class="col-xs-4">
      <div
        v-if="loading.items || loading.categories"
        class="width-100 display-flex justify-content-center"
      >
        <i
          class="margin-top-20 fa fa-spinner fa-pulse fa-fw"
        />
      </div>
      <div
        v-else
        id="nestable_list_2"
        class="dd"
      >
        <ol class="dd-list">
          <li
            v-for="category in activeMenu.categories"
            v-if="category.parent_category === 0"
            :key="category.id"
            class="dd-item"
            :data-id="category.id"
          >
            <div
              class="dd-handle pointer v-center"
              :class="{'active': category.id === activeCategory.id}"
              @click="toggleCategory(category)"
            >
              <div class="v-center">
                <i
                  class="fa check"
                  :class="{
                    'transparent' : !categorySelectable(category),
                    'fa-check-square checked': categorySelected(category),
                    'fa-minus-square checked': categoryPartiallySelected(category),
                    'fa-square-o unchecked': !categorySelected(category)
                  }"
                  aria-hidden="true"
                  @click.stop="toggleAllInCategory(category)"
                />
                {{ category.name }}
              </div>
              <span
                class="pull-right transparent"
                :class="{'permanent' : category.id === activeCategory.id}"
              >
                <i class="fa fa-chevron-right" />
              </span>
            </div>
          </li>
          <p
            v-show="activeMenu.id !== undefined && activeMenu.categories !== undefined && !activeMenu.categories.length"
            class="no-items"
          >
            This menu is empty
          </p>
        </ol>
      </div>
    </div>
    <div
      v-show="activeCategory.id !== undefined"
      class="col-xs-4"
    >
      <div
        id="nestable_list_3"
        class="dd"
      >
        <ol class="dd-list">
          <li
            v-for="item in activeItems"
            :key="item.id"
            class="dd-item"
            @click="toggleItem(item)"
          >
            <div class="dd-handle pointer v-center">
              <div class="v-center">
                <i
                  class="fa check"
                  :class="{
                    'fa-check-square checked': item.selected,
                    'fa-square-o unchecked': !item.selected
                  }"
                  aria-hidden="true"
                />
                <div>
                  <div>
                    {{ item.name }}
                    {{ item.category.parent_category !== 0 ? ` (${item.category_name})` : '' }}
                  </div>
                  <div
                    v-show="showSku"
                    class="grey"
                  >
                    {{ `${item.sku}` }}
                  </div>
                </div>
              </div>
            </div>
          </li>
          <p
            v-show="!activeItems.length"
            class="no-items"
          >
            This category is empty
          </p>
        </ol>
      </div>
    </div>
  </div>
</template>

<script>
import MenusFunctions from '@/controllers/Menus'
import CategoriesFunctions from '@/controllers/Categories'
import ajaxErrorHandler from '@/controllers/ErrorController'

export default {
  name: 'MenuItemPicker',
  props: {
    /**
     * @property {array} previouslySelected - An array of SKU strings of previously selected items
     */
    previouslySelected: {
      type: Array,
      default: () => [],
      required: false
    },
    /**
     * @property {boolean} single - Limits selection to one item only
     */
    single: {
      type: Boolean,
      default: () => false,
      required: false
    },
    /**
     * @property {boolean} single - Displays item's SKU below the item
     */
    showSku: {
      type: Boolean,
      default: () => false,
      required: false
    }
  },
  data: () => ({
    loading: {
      menus: false,
      categories: false,
      items: false
    },
    previous: [],
    errorMessage: '',
    menus: [],
    activeMenu: {
      categories: [],
      items: []
    },
    activeCategory: {}
  }),
  computed: {
    activeItems () {
      if (this.activeMenu.items) {
        return this.activeMenu.items.filter(item => {
          return item.category.parent_category === 0 ? item.category.id === this.activeCategory.id : item.category.parent_category === this.activeCategory.id
        })
      } else {
        return []
      }
    },
    allCategoriesSelected () {
      if (this.activeMenu.categories && this.activeMenu.items) {
        return (
          this.activeMenu.items.length &&
          !this.activeMenu.items.some(item => !item.selected)
        )
      } else {
        return false
      }
    },
    allItemsSelected () {
      return (
        this.activeItems.length &&
        !this.activeItems.some(item => !item.selected)
      )
    },
    infoMessage () {
      if (this.$root.activeLocation.id === undefined) {
        return 'Please select a store to view its Menus.'
      } else {
        return ''
      }
    }
  },
  watch: {
    '$root.activeLocation': function (location) {
      if (location.id !== undefined) {
        this.getMenus()
      }
    }
  },
  created () {
    this.previous = [...this.previouslySelected]
    if (this.$root.activeLocation.id !== undefined) {
      this.getMenus()
    }
  },
  methods: {
    /**
     * To change selection an item
     * @function
     * @param {object} item - The item to toggle
     * @returns {undefined}
     */
    toggleItem (item) {
      if (this.single) {
        this.activeMenu.items.forEach(menuItem => {
          if (item.id !== menuItem.id) {
            menuItem.selected = false
          }
        })
        this.menus.forEach(menu => {
          if (this.menuPartiallySelected(menu) || this.menuSelected(menu)) {
            menu.items.forEach(item => {
              item.selected = false
            })
          }
        })
      }
      if (item.selected) {
        const previousIndex = this.previous.indexOf(item.sku)
        if (previousIndex !== -1) {
          this.previous.splice(previousIndex, 1)
        }
      }
      item.selected = !item.selected
      this.update()
    },
    /**
     * To toggle selection of all items
     * @function
     * @returns {undefined}
     */
    toggleAllItems () {
      if (this.single) return
      const selected = this.allItemsSelected
      this.activeItems.forEach(item => {
        item.selected = !selected
      })
      if (selected) {
        this.activeItems.forEach(item => {
          const previousIndex = this.previous.indexOf(item.sku)
          if (previousIndex !== -1) {
            this.previous.splice(previousIndex, 1)
          }
        })
      }
      this.update()
    },
    /**
     * To change the active category
     * @function
     * @param {object} category - Selected category
     * @returns {undefined}
     */
    toggleCategory (category) {
      this.activeCategory = category
      this.update()
    },
    /**
     * To toggle selection of all categories
     * @function
     * @returns {undefined}
     */
    toggleAllCategories () {
      if (this.single) return
      const selected = this.allCategoriesSelected
      this.activeMenu.items.forEach(item => {
        item.selected = !selected
      })
      if (selected) {
        this.activeMenu.items.forEach(item => {
          const previousIndex = this.previous.indexOf(item.sku)
          if (previousIndex !== -1) {
            this.previous.splice(previousIndex, 1)
          }
        })
      }
      this.update()
    },
    /**
     * To select a menu and fetch its items
     * @function
     * @param {object} menu - The selected menu.
     * @returns {undefined}
     */
    toggleMenu (menu) {
      this.activeCategory = {}
      this.activeMenu = menu
      if (menu.items === undefined) {
        return Promise.all([
          this.getAllItemsInMenu(),
          this.getMenuCategories()
        ])
      }
    },
    /**
     * To change selection of all items in a menu
     * @function
     * @param {object} menu - The menu of items to toggle
     * @returns {undefined}
     */
    toggleAllInMenu (menu) {
      if (this.single) return
      const selected = this.menuSelected(menu)
      if (menu.items) {
        menu.items.forEach(item => {
          if (selected) {
            const previousIndex = this.previous.indexOf(item.sku)
            if (previousIndex !== -1) {
              this.previous.splice(previousIndex, 1)
            }
          }
          item.selected = !selected
        })
        this.update()
      } else {
        this.toggleMenu(menu).then(() => {
          this.activeMenu.items.forEach(item => {
            if (selected) {
              const previousIndex = this.previous.indexOf(item.sku)
              if (previousIndex !== -1) {
                this.previous.splice(previousIndex, 1)
              }
            }
            item.selected = !selected
          })
          this.update()
        })
      }
    },
    /**
     * To change selection of all items in a category
     * @function
     * @param {object} category - The category of items to toggle
     * @returns {undefined}
     */
    toggleAllInCategory (category) {
      if (this.single) return
      const selected = this.categorySelected(category)
      let menu = this.menus.filter(menu => menu.id === category.menu_id)[0]
      let items = menu.items.filter(item => {
        return item.category.parent_category === 0 ? item.category.id === category.id : item.category.parent_category === category.id
      })
      items.forEach(item => {
        if (selected) {
          const previousIndex = this.previous.indexOf(item.sku)
          if (previousIndex !== -1) {
            this.previous.splice(previousIndex, 1)
          }
        }
        item.selected = !selected
      })
      if (this.activeCategory.id !== category.id) {
        this.toggleCategory(category)
      }
      this.update()
    },
    /**
     * To clear an error variable
     * @function
     * @param {string} name - Name of the variable to clear
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    clearError (name) {
      this[name] = ''
    },
    /**
     * To get a list of all menus for the current active location
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getMenus () {
      this.loading.menus = true
      this.menus = []
      var pickerVue = this
      return MenusFunctions.getStoreMenus(
        pickerVue.$root.appId,
        pickerVue.$root.appSecret,
        pickerVue.$root.activeLocation.id,
        {
          all_type: 1
        }
      )
        .then(response => {
          pickerVue.menus = response.payload.map(menu => {
            return {
              ...menu,
              selected: false,
              indeterminate: false
            }
          })
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not get menus',
            errorName: 'errorMessage',
            vue: pickerVue
          })
        })
        .finally(() => {
          pickerVue.loading.menus = false
        })
    },
    /**
     * To get a list of categories for a menu.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getMenuCategories () {
      this.loading.categories = true
      let pickerVue = this
      return CategoriesFunctions.getMenuCategories(this.activeMenu.id)
        .then(response => {
          let categories = response.payload.map(category => ({
            ...category,
            selected: false
          }))

          pickerVue.$set(pickerVue.activeMenu, 'categories', categories)
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not get categories',
            errorName: 'errorMessage',
            vue: pickerVue
          })
        })
        .finally(() => {
          pickerVue.loading.categories = false
        })
    },
    /**
     * To get a list of all items for a menu.
     * @function
     * @returns {object} - A promise that will either return an error message or perform an action.
     */
    getAllItemsInMenu () {
      this.loading.items = true
      var pickerVue = this
      return MenusFunctions.getAllItemsInMenu(pickerVue.activeMenu.id)
        .then(response => {
          let menu = pickerVue.menus.filter(
            menu => menu.id === pickerVue.activeMenu.id
          )[0]
          let items = response.payload.map(item => {
            return {
              ...item,
              selected: pickerVue.previous.includes(item.sku)
            }
          })
          pickerVue.$set(menu, 'items', items)
        })
        .catch(reason => {
          ajaxErrorHandler({
            reason,
            errorText: 'We could not get menu items',
            errorName: 'errorMessage',
            vue: pickerVue
          })
        })
        .finally(() => {
          pickerVue.loading.items = false
        })
    },
    /**
     * To determine if a menu contains items to select
     * @function
     * @param {object} menu - The menu to check
     * @returns {boolean} True if it contains items, false if not
     */
    menuSelectable (menu) {
      if (!menu.items) {
        return false
      } else {
        return menu.items.length !== 0
      }
    },
    /**
     * To determine if a category contains items to select
     * @function
     * @param {object} category - The category to check
     * @returns {boolean} True if it contains items, false if not
     */
    categorySelectable (category) {
      let menu = this.menus.filter(menu => menu.id === category.menu_id)[0]
      return menu.items.length !== 0
    },
    /**
     * To determine if a menu is selected
     * @function
     * @param {object} menu - The menu to check
     * @returns {boolean} True if it's selected, false if not
     */
    menuSelected (menu) {
      if (menu.items) {
        return !menu.items.some(item => !item.selected)
      } else {
        return false
      }
    },
    /**
     * To determine if a menu is partially selected (indeterminate)
     * @function
     * @param {object} menu - The menu to check
     * @returns {boolean} True if it's partially selected, false if not
     */
    menuPartiallySelected (menu) {
      if (menu.items) {
        let itemsLength = menu.items.length
        let selectedItems = menu.items.filter(item => item.selected)
        let selectedItemsLength = selectedItems.length
        return selectedItemsLength !== 0 && selectedItemsLength !== itemsLength
      } else {
        return false
      }
    },
    /**
     * To determine if a category is selected
     * @function
     * @param {object} category - The category to check
     * @returns {boolean} True if it's selected, false if not
     */
    categorySelected (category) {
      let menu = this.menus.filter(menu => menu.id === category.menu_id)[0]
      let items = menu.items.filter(item => {
        return item.category.parent_category === 0 ? item.category.id === category.id : item.category.parent_category === category.id
      })
      return items.length && !items.some(item => !item.selected)
    },
    /**
     * To determine if a category is partially selected (indeterminate)
     * @function
     * @param {object} category - The category to check
     * @returns {boolean} True if it's partially selected, false if not
     */
    categoryPartiallySelected (category) {
      let menu = this.menus.filter(menu => menu.id === category.menu_id)[0]
      let items = menu.items.filter(item => {
        return item.category.parent_category === 0 ? item.category.id === category.id : item.category.parent_category === category.id
      })
      let itemsLength = items.length
      let selectedItems = items.filter(item => item.selected)
      let selectedItemsLength = selectedItems.length
      return selectedItemsLength !== 0 && selectedItemsLength !== itemsLength
    },
    /**
     * To notify the parent item selection has changed
     * @function
     * @returns {undefined}
     */
    update () {
      let selected = []
      let menusWithItems = this.menus.filter(menu => menu.items !== undefined)
      menusWithItems.forEach(menu => {
        let selectedMenuItems = menu.items.filter(item => item.selected)
        selected = selected.concat(selectedMenuItems)
      })
      selected = [
        ...this.previous.map(sku => ({ previous: true, sku, name: '' })),
        ...selected.filter(item => !this.previous.includes(item.sku))
      ]
      this.$emit('update', selected)
    }
  }
}
</script>
<style scoped lang="scss">
.pointer {
  cursor: default;
}
.active,
i {
  color: #2ea8e5;
}
.transparent {
  transition: opacity 0.3s;
  opacity: 0;
}
.dd-item:hover .pull-right {
  opacity: 1;
}
.permanent {
  opacity: 1;
}
.header {
  padding-left: 13px;
  h4 {
    padding: 0 5px;
  }
}
.no-items {
  margin: 0;
  padding-left: 20px;
  height: 30px;
  display: flex;
  align-items: center;
  color: #ccc;
}
.v-center {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: auto;
}
.width-100 {
  width: 100%;
}
.display-flex {
  display: flex;
}
.justify-content-center {
  justify-content: center;
}
.grey {
  color: #5f5f5f;
}
</style>
